// client/src/services/api.js - VERSÃO COM API CLIENT E REFRESH TOKEN
import axios from 'axios';
import { getValidAccountId, isMatrizAdmin } from '../utils/permissions';
import { useAuth } from '../context/auth/AuthProvider';

// ✅ Configuração base do axios
const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:50000/api/v1',
    headers: {
        'Content-Type': 'application/json',
        // ✅ SEGURANÇA: identifica toda chamada como vinda do painel web —
        // o backend usa isso (middlewares/auth.js::protect) pra bloquear
        // scope 'conta' aqui (painel exclusivo pra matriz/franquia; contas
        // usam o app mobile). Reforço server-side do mesmo limite que
        // PrivateRoute.js já aplica client-side.
        'X-Client-Platform': 'web',
    },
    timeout: 30000,
});

// ✅ FILA DE REQUISIÇÕES PARA REFRESH TOKEN
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// ✅ INTERCEPTOR DE REQUISIÇÃO
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // ✅ Adicionar accountId para Matriz Admin — exceto nas rotas de
        // escopo global da própria matriz (institucional, políticas,
        // templates). Essas telas devem sempre mostrar os registros
        // account:null/franquia:null da matriz, nunca os de "conta
        // selecionada" — injetar accountId aqui fazia a query do backend
        // ficar contraditória (account:null E accountId:<algo> ao mesmo
        // tempo) e a lista sempre voltava vazia pra um admin matriz com uma
        // conta selecionada/vinculada.
        // Normaliza a barra inicial — usePolicies.js chama 'admin/policies'
        // (sem barra) enquanto os demais hooks chamam '/admin/...' (com
        // barra); sem isso o startsWith abaixo não bate pra políticas.
        const normalizedUrl = '/' + (config.url || '').replace(/^\/+/, '');
        // ✅ CORREÇÃO: /auditorias é rota matriz-only (authorize('matriz') em
        // auditsRoutes.js — Audit nem tem campo account/accountId no schema).
        // Sem estar na lista, um admin matriz com uma conta selecionada
        // injetava accountId=<...> em toda chamada GET /auditorias, e o
        // Mongo passava a filtrar por um campo inexistente — 0 resultados
        // sempre, silenciosamente, mesmo com auditorias reais no banco.
        const isMatrizOwnScopeRoute = [
            '/admin/institucional/',
            '/admin/policies',
            '/templates/mensagem',
            '/templates/recibo',
            '/auditorias',
            '/logs'
        ].some(prefix => normalizedUrl.startsWith(prefix));

        // ✅ Adicionar accountId para Matriz Admin
        try {
            const userData = localStorage.getItem('user');
            if (userData && !isMatrizOwnScopeRoute) {
                const user = JSON.parse(userData);

                if (isMatrizAdmin(user)) {
                    const accountId = getValidAccountId(user);
                    if (accountId) {
                        if (config.method === 'get') {
                            config.params = {
                                ...config.params,
                                accountId: accountId
                            };
                        } else if (config.data instanceof FormData) {
                            // FormData não é um objeto plano — {...formData} não
                            // copia os campos/arquivos anexados via .append()
                            // (não são propriedades enumeráveis), só devolve um
                            // objeto vazio. O spread abaixo (ramo `else if
                            // (config.data)`) substituía o FormData inteiro por
                            // {accountId: '...'}, jogando fora o arquivo — todo
                            // upload (banner, imagem de conteúdo, etc.) de um
                            // admin matriz com accountId ativo quebrava
                            // silenciosamente antes mesmo de chegar no servidor.
                            config.data.append('accountId', accountId);
                        } else if (config.data) {
                            config.data = {
                                ...config.data,
                                accountId: accountId
                            };
                        }

                        console.log(`✅ [API] Matriz Admin: accountId ${accountId} adicionado`);
                    } else {
                        console.warn('⚠️ [API] Matriz Admin sem accountId disponível');
                    }
                }
            }
        } catch (error) {
            console.error('❌ [API] Erro ao processar dados do usuário:', error);
        }
        
        if (process.env.NODE_ENV === 'development') {
            console.log('🌐 [API Request]', {
                method: config.method?.toUpperCase(),
                url: config.url,
                params: config.params,
                data: config.data
            });
        }
        
        return config;
    },
    (error) => {
        console.error('❌ [API Request Error]', error);
        return Promise.reject(error);
    }
);

// ✅ INTERCEPTOR DE RESPOSTA COM REFRESH TOKEN
apiClient.interceptors.response.use(
    (response) => {
        if (process.env.NODE_ENV === 'development') {
            console.log('✅ [API Response]', {
                status: response.status,
                url: response.config.url,
                data: response.data
            });
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        
        // ✅ Se erro 401 e não é uma tentativa de refresh, tentar renovar token
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return apiClient(originalRequest);
                }).catch(err => Promise.reject(err));
            }

            isRefreshing = true;

            // O servidor usa JWT único (sem refresh token separado).
            // O endpoint /auth/refresh-token usa req.user do middleware protect,
            // portanto enviamos o token atual no header para que o servidor possa
            // verificar a identidade e emitir um novo JWT.
            const currentToken = localStorage.getItem('token') || sessionStorage.getItem('token');

            if (!currentToken) {
                isRefreshing = false;
                if (window.location.pathname !== '/login') {
                    window.location.href = '/login?session=expired';
                }
                return Promise.reject(error);
            }

            try {
                const response = await axios.post(
                    `${apiClient.defaults.baseURL}/auth/refresh-token`,
                    {},
                    { headers: { Authorization: `Bearer ${currentToken}`, 'Content-Type': 'application/json' } }
                );
                const { token } = response.data;

                localStorage.setItem('token', token);
                if (sessionStorage.getItem('token')) sessionStorage.setItem('token', token);

                apiClient.defaults.headers.common.Authorization = `Bearer ${token}`;
                processQueue(null, token);

                originalRequest.headers.Authorization = `Bearer ${token}`;
                return apiClient(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);

                localStorage.removeItem('token');
                sessionStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
                sessionStorage.removeItem('refreshToken');
                localStorage.removeItem('user');

                if (window.location.pathname !== '/login') {
                    window.location.href = '/login?session=expired';
                }
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }
        
        // ✅ ACHADO CRÍTICO: este bloco fazia um window.location.href pra
        // /acesso-negado em QUALQUER 403 de QUALQUER requisição concorrente,
        // sem relação nenhuma com a rota que o usuário está vendo. Um admin
        // de matriz abrindo /franquias (rota que ele PODE acessar) era
        // ejetado com hard-reload sempre que alguma OUTRA requisição em
        // paralelo — ex.: um widget da sidebar batendo num endpoint de
        // escopo conta que a matriz legitimamente não vê — respondia 403
        // primeiro. Isso já é redundante: o controle de acesso por rota
        // acontece em PrivateRoute.js (hasScopeAccess), que já navega pra
        // /acesso-negado quando A ROTA ATUAL exige permissão que o usuário
        // não tem. Aqui, 403 de uma chamada específica só precisa propagar
        // pro caller — mutations já tratam via onError/setAlert (padrão do
        // CLAUDE.md), e isError das queries fica disponível pra quem
        // precisar exibir algo.
        if (error.response?.status === 403) {
            console.warn('⚠️ [API] Acesso negado (403) em', originalRequest?.url);
        }

        console.error('❌ [API Error]', {
            status: error.response?.status,
            message: error.message,
            url: originalRequest?.url,
            backendMessage: error.response?.data?.message || error.response?.data?.error,
            backendData: error.response?.data
        });
        
        return Promise.reject(error);
    }
);

// ✅ FUNÇÕES AUXILIARES
export const addAccountIdToParams = (params = {}, user) => {
    if (!user) return params;
    if (isMatrizAdmin(user)) {
        const accountId = getValidAccountId(user);
        if (accountId) {
            return { ...params, accountId: accountId };
        }
    }
    return params;
};

export const addAccountIdToBody = (data = {}, user) => {
    if (!user) return data;
    if (isMatrizAdmin(user)) {
        const accountId = getValidAccountId(user);
        if (accountId) {
            return { ...data, accountId: accountId };
        }
    }
    return data;
};

export const useApi = () => {
    const { user } = useAuth();
    
    const apiWithAccount = {
        get: (url, config = {}) => {
            const newConfig = { ...config, params: addAccountIdToParams(config.params, user) };
            return apiClient.get(url, newConfig);
        },
        post: (url, data = {}, config = {}) => {
            const newData = addAccountIdToBody(data, user);
            return apiClient.post(url, newData, config);
        },
        put: (url, data = {}, config = {}) => {
            const newData = addAccountIdToBody(data, user);
            return apiClient.put(url, newData, config);
        },
        patch: (url, data = {}, config = {}) => {
            const newData = addAccountIdToBody(data, user);
            return apiClient.patch(url, newData, config);
        },
        delete: (url, config = {}) => {
            const newConfig = { ...config, params: addAccountIdToParams(config.params, user) };
            return apiClient.delete(url, newConfig);
        },
        request: apiClient.request,
        head: apiClient.head,
        options: apiClient.options
    };
    
    return apiWithAccount;
};

export const makeRequest = (method, url, data = {}, params = {}) => {
    return apiClient.request({ method, url, data, params });
};

export { apiClient };
export default apiClient;